import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";

import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { useAuth } from "../../AuthContext";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { InputSwitch } from "primereact/inputswitch";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { addLocale, locale } from "primereact/api";

import "primeicons/primeicons.css";
import FecharCaixa from "./../Caixa/FecharCaixa";
import "./style.css";

// Importando os diálogos
import DialogSucesso from "./DialogSucesso";
import DialogErro from "./DialogErro";
import DialogCancelar from "./DialogCancelar";
import DialogRealizarAtendimento from "./DialogRealizarAtendimento";
import DialogHistoricoPaciente from "./DialogHistoricoPaciente";
import DialogNovaAgenda from "./DialogNovaAgenda";
import DialogEditarAgenda from "./DialogEditarAgenda";
import DialogAgendarRetorno from "./DialogAgendarRetorno";
import DialogFichaPaciente from "./DialogFichaPaciente";
import DialogPesquisarPaciente from "./DialogPesquisarPaciente";
import CondicaoSelector from "./CondicaoSelector";
import DialogConfirmarAtendimento from "./DialogConfirmarAtendimento";

// Importando componentes memoizados
import DentesAdultoMemo from "./DentesAdultoMemo";
import DentesInfantilMemo from "./DentesInfantilMemo";

// Ícones
import {
  FaCalendarAlt,
  FaCalendarPlus,
  FaUserMd,
  FaBuilding,
  FaList,
  FaCalendarCheck,
  FaStickyNote,
  FaFileMedical,
  FaTooth,
  FaTag,
  FaUser,
  FaMapMarkerAlt,
  FaClock,
  FaCalendar,
  FaDollarSign,
  FaChartBar,
  FaHistory,
  FaUserCircle,
  FaIdCard,
  FaPhone,
  FaEnvelope,
  FaUserFriends,
  FaFileAlt,
  FaHeartbeat,
  FaSearch,
  FaCashRegister,
  FaShoppingCart,
  FaPlusCircle,
  FaMinusCircle,
  FaCreditCard,
} from "react-icons/fa";

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
const Agendas = () => {
  const { user } = useAuth();
  const agendamentoIdRef = useRef(null);

  // ==========================================================================
  // HOOKS DE MEDIA QUERY PARA MOBILE
  // ==========================================================================
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // ==========================================================================
  // ESTADOS GERAIS
  // ==========================================================================
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agendamentos, setAgendamentos] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [visualizacaoResumida, setVisualizacaoResumida] = useState(true);
  const [visiblePesquisar, setVisiblePesquisar] = useState(false);
  const [visibleFicha, setVisibleFicha] = useState(false);

  // ==========================================================================
  // ESTADOS PARA DIÁLOGOS DE CONFIRMAÇÃO
  // ==========================================================================
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");

  // ==========================================================================
  // ESTADOS PARA EDIÇÃO EM DIÁLOGO
  // ==========================================================================
  const [visibleEditar, setVisibleEditar] = useState(false);
  const [visibleRetorno, setVisibleRetorno] = useState(false);
  const [reservaEditando, setReservaEditando] = useState(null);
  const [data, setData] = useState("");
  const [hora, setHora] = useState("");
  const [profissional, setProfissional] = useState("");
  const [servico, setServico] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [visibleAdicionar, setVisibleAdicionar] = useState(false);

  // ==========================================================================
  // ESTADOS DO PAGAMENTO
  // ==========================================================================
  const [pagamentoSelecionado, setPagamentoSelecionado] = useState(null);
  const [pagamentosAdicionados, setPagamentosAdicionados] = useState([]);
  const [formaPagamentoSelecionada, setFormaPagamentoSelecionada] =
    useState(null);
  const [showConfirmImpressao, setShowConfirmImpressao] = useState(false);

  // ==========================================================================
  // ESTADOS DO CALENDÁRIO
  // ==========================================================================
  const [diaParaNovoAgendamento, setDiaParaNovoAgendamento] = useState(null);
  const [dataVeioDoCalendario, setDataVeioDoCalendario] = useState(false);

  // ==========================================================================
  // ESTADOS DO ODONTOGRAMA
  // ==========================================================================
  const [historicoVisualMap, setHistoricoVisualMap] = useState({});
  const [condicoesVisualHistorico, setCondicoesVisualHistorico] = useState({});
  const [historicoAgendamentoAtual, setHistoricoAgendamentoAtual] = useState(
    {},
  );
  const [historicoDentesMap, setHistoricoDentesMap] = useState({});
  const [condicoesDentes, setCondicoesDentes] = useState({});
  const [historicoGlobalPaciente, setHistoricoGlobalPaciente] = useState({});
  const [mostrarAdulto, setMostrarAdulto] = useState(true);
  const [mostrarInfantil, setMostrarInfantil] = useState(false);
  const [visibleCondicaoDialog, setVisibleCondicaoDialog] = useState(false);
  const [denteSelecionadoParaCondicao, setDenteSelecionadoParaCondicao] =
    useState(null);
  const [agendamentoAtualId, setAgendamentoAtualId] = useState(null);

  // ==========================================================================
  // ESTADOS PARA NOVO PACIENTE
  // ==========================================================================
  const [novoPaciente, setNovoPaciente] = useState({
    nome: "",
    cpf: "",
    telefone: "",
    email: "",
  });

  // ==========================================================================
  // ESTADOS DOS DENTES SELECIONADOS
  // ==========================================================================
  const [selectedDentesAdulto, setSelectedDentesAdulto] = useState([]);
  const [selectedDentesInfantil, setSelectedDentesInfantil] = useState([]);
  const [dentesProcessados, setDentesProcessados] = useState([]);
  const [observacoes, setObservacoes] = useState("");
  const [queixas, setQueixas] = useState("");
  const [observacoesExistentes, setObservacoesExistentes] = useState([]);
  const [tipoDente, setTipoDente] = useState("adulto");

  // ==========================================================================
  // ESTADOS DO HISTÓRICO
  // ==========================================================================
  const [visibleHistorico, setVisibleHistorico] = useState(false);
  const [itensExpandidos, setItensExpandidos] = useState([]);
  const [pacienteEscolhido, setPacienteEscolhido] = useState("");

  // ==========================================================================
  // ESTADOS DO ATENDIMENTO
  // ==========================================================================
  const [dialogFinalizar, setDialogFinalizar] = useState(false);
  const [checked, setChecked] = useState(false);
  const [caixaConfirmationSuccess, setCaixaConfirmationSuccess] =
    useState(false);

  // ==========================================================================
  // ESTADOS DO CAIXA
  // ==========================================================================
  const [visibleCaixa, setVisibleCaixa] = useState(false);
  const [mostrarAjustes, setMostrarAjustes] = useState(false);
  const [acrescimo, setAcrescimo] = useState("");
  const [desconto, setDesconto] = useState("");

  // ==========================================================================
  // ESTADOS DAS DESCRIÇÕES DENTÁRIAS
  // ==========================================================================
  const [descricaoAtual, setDescricaoAtual] = useState("");
  const [descricoesDentarias, setDescricoesDentarias] = useState([]);

  // ==========================================================================
  // ESTADOS PARA CANCELAR
  // ==========================================================================
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showConfirmCaixa, setShowConfirmCaixa] = useState(false);
  const [showConfirmFinalizarAposCriacao, setShowConfirmFinalizarAposCriacao] =
    useState(false);
  const [agendamentoCriadoId, setAgendamentoCriadoId] = useState(null);
  const [itemCancela, setItemCancela] = useState(null);

  // ==========================================================================
  // ESTADOS PRINCIPAIS SIMPLIFICADOS
  // ==========================================================================
  const [dadosCompletos, setDadosCompletos] = useState({
    empresas: [],
    profissionais: [],
    servicos: [],
    datas_atendimento: [],
    horarios: [],
    agendamentos_existentes: [],
  });

  // ==========================================================================
  // ESTADOS PARA FILTROS EM CASCATA
  // ==========================================================================
  const [mostrarCalendario, setMostrarCalendario] = useState(false);
  const [profissionaisFiltrados, setProfissionaisFiltrados] = useState([]);
  const [datasDisponiveis, setDatasDisponiveis] = useState([]);
  const [horariosDisponiveis, setHorariosDisponiveis] = useState([]);
  const [servicosFiltrados, setServicosFiltrados] = useState([]);
  const [dialogInformações, setDialogInformações] = useState(false);

  // ==========================================================================
  // ESTADOS PARA SERVIÇOS
  // ==========================================================================
  const [servicosSelecionados, setServicosSelecionados] = useState([]);
  const [servicoSelecionadoId, setServicoSelecionadoId] = useState(null);
  const [valorTotal, setValorTotal] = useState("");

  // ==========================================================================
  // ESTADOS PARA DATAS
  // ==========================================================================
  const [diaSelecionado, setDiaSelecionado] = useState(new Date());
  const [mesCalendario, setMesCalendario] = useState(new Date());

  // ==========================================================================
  // ESTADOS PARA FILTROS DE PESQUISA
  // ==========================================================================
  const [pesquisarNome, setPesquisarNome] = useState("");
  const [pesquisarCpf, setPesquisarCpf] = useState("");
  const [pacientesFiltrados, setPacientesFiltrados] = useState([]);
  const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
  const [todasDatasDisponiveis, setTodasDatasDisponiveis] = useState([]);

  // ==========================================================================
  // ESTADOS PARA FILTRO DA AGENDA
  // ==========================================================================
  const [mostrarFiltroAgenda, setMostrarFiltroAgenda] = useState(false);
  const [filtroNome, setFiltroNome] = useState("");
  const [filtroCpf, setFiltroCpf] = useState("");
  const [filtroTelefone, setFiltroTelefone] = useState("");
  const [filtroEmail, setFiltroEmail] = useState("");

  // ==========================================================================
  // ESTADOS DA FICHA
  // ==========================================================================
  const [fichaData, setFichaData] = useState({
    tem_problema_saude: false,
    qual_problema_saude: "",
    em_tratamento_medico: false,
    motivo_tratamento_medico: "",
    usa_medicamento: false,
    quais_medicamentos: "",
    alergia_medicamento: false,
    quais_alergias: "",
    pressao_arterial: "",
    fumante: false,
    gengiva_sangra: false,
    sensibilidade_dentes: false,
    escovacoes_por_dia: null,
    usa_fio_dental: false,
    frequencia_fio_dental: "",
    roi_unhas: false,
    morde_lapis: false,
    outros_habitos: "",
    higiene_bucal: "Regular",
    halitose: "Ausente",
    mucosa: "Normal",
    lingua: "",
    palato: "",
    palato_mole: "",
    assoalho_bucal: "",
    labios: "",
    placa_bacteriana: false,
    sangramento_gengival: false,
    tartaro: false,
    mobilidade_dental: false,
    apinhamento: "Ausente",
    diastemas: false,
    observacoes: "",
    plano_tratamento: "",
    data_assinatura: null,
    mes_assinatura: "",
    ano_assinatura: "",
  });

  // ==========================================================================
  // CONFIGURAÇÃO DO LOCALE
  // ==========================================================================
  addLocale("pt", {
    firstDayOfWeek: 0,
    dayNames: [
      "Domingo",
      "Segunda",
      "Terça",
      "Quarta",
      "Quinta",
      "Sexta",
      "Sábado",
    ],
    dayNamesShort: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
    dayNamesMin: ["D", "S", "T", "Q", "Q", "S", "S"],
    monthNames: [
      "Janeiro",
      "Fevereiro",
      "Março",
      "Abril",
      "Maio",
      "Junho",
      "Julho",
      "Agosto",
      "Setembro",
      "Outubro",
      "Novembro",
      "Dezembro",
    ],
    monthNamesShort: [
      "Jan",
      "Fev",
      "Mar",
      "Abr",
      "Mai",
      "Jun",
      "Jul",
      "Ago",
      "Set",
      "Out",
      "Nov",
      "Dez",
    ],
    today: "Hoje",
    clear: "Limpar",
  });
  locale("pt");

  // ==========================================================================
  // FUNÇÕES DE API
  // ==========================================================================

  // Função para buscar agendamentos
  const fetchAgendamentos = async (showAll = false) => {
    setLoading(true);
    setError(null);

    try {
      if (!user?.token) {
        throw new Error("Usuário não autenticado. Faça login novamente.");
      }

      const url =
        "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/agendamentos";

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Erro ao buscar agendas");
      }

      const result = await response.json();

      // console.log("result.data = ", result.data);

      if (result.success && Array.isArray(result.data)) {
        let filteredData = result.data;

        if (!showAll) {
          filteredData = result.data.filter(
            (agenda) => agenda.status === "confirmado",
          );
        } else {
          filteredData = result.data.filter(
            (agenda) => agenda.status_reserva !== "pendente",
          );
        }

        setAgendamentos(filteredData);
        setError(null);
      } else {
        setAgendamentos([]);
        setError("Nenhuma agenda encontrada.");
      }
    } catch (error) {
      setError(error.message);
      setAgendamentos([]);
    } finally {
      setLoading(false);
    }
  };
  // Função para buscar todos os dados (empresas, profissionais, serviços)
  const fetchTodosDados = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/agendamento/dados",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            Accept: "application/json",
          },
        },
      );

      if (!response.ok)
        throw new Error(`HTTP ${response.status} - ${response.statusText}`);

      const result = await response.json();

      // console.log("result.data = ", result.data);

      if (result.success && result.data) {
        setDadosCompletos({
          empresas: result.data.empresas || [],
          profissionais: result.data.profissionais || [],
          servicos: result.data.servicos || [],
          datas_atendimento: result.data.datas_atendimento || [],
          horarios: result.data.horarios || [],
          agendamentos_existentes: result.data.agendamentos_existentes || [],
        });

        // CARREGAR TODAS AS DATAS DISPONÍVEIS
        const todasDatas = [
          ...new Set(
            result.data.datas_atendimento.map((d) => d.data_atendimento),
          ),
        ];
        const datasFormatadas = todasDatas
          .map((data) => ({
            label: new Date(data + "T00:00:00").toLocaleDateString("pt-BR"),
            value: data,
          }))
          .sort((a, b) => a.value.localeCompare(b.value));

        setTodasDatasDisponiveis(datasFormatadas);
        setDatasDisponiveis(datasFormatadas);

        // Inicializar profissionais filtrados
        setProfissionaisFiltrados(
          result.data.profissionais.map((prof) => ({
            label: prof.nome,
            value: prof.id,
          })),
        );

        // Inicializar serviços filtrados
        setServicosFiltrados(
          result.data.servicos.map((serv) => ({
            label: `${serv.nome} - ${serv.duracao}min - R$ ${parseFloat(serv.valor).toFixed(2)}`,
            value: serv.id,
            nome: serv.nome,
            duracao: serv.duracao,
            valor: parseFloat(serv.valor).toFixed(2),
            descricao: serv.nome,
          })),
        );
      } else {
        throw new Error(result.message || "Erro na estrutura dos dados");
      }
    } catch (error) {
      console.error("❌ Erro ao carregar dados:", error);
      setError(error.message);
      setDadosCompletos({
        empresas: [],
        profissionais: [],
        servicos: [],
        datas_atendimento: [],
        horarios: [],
        agendamentos_existentes: [],
      });
    } finally {
      setLoading(false);
    }
  };
  const fetchPacientes = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!user?.token) {
        throw new Error("Usuário não autenticado. Faça login novamente.");
      }

      const response = await fetch(
        "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/pacientes",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Erro ao buscar pacientes");
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.data)) {
        setPacientes(data.data);
        setError(null);
      } else {
        setPacientes([]);
        setError("Nenhum paciente encontrado.");
      }
    } catch (error) {
      setError(error.message);
      setPacientes([]);
    } finally {
      setLoading(false);
    }
  };
  const buscarFichaPaciente = async (pacienteId, agendamentoIdAtual = null) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/agendas/${pacienteId}/paciente`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            Accept: "application/json",
          },
        },
      );

      if (!response.ok)
        throw new Error(`HTTP ${response.status} - ${response.statusText}`);

      const data = await response.json();

      if (data.success && data.data) {
        setPacienteEscolhido(data.data);

        // 🔥 1. Processar HISTÓRICO VISUAL COMPLETO (situações)
        const historicoVisual = {};

        // 🔥 2. Processar CONDIÇÕES VISUAIS COMPLETAS (de TODOS os agendamentos)
        const condicoesVisualCompleto = {};

        data.data.forEach((item) => {
          // Processar situações (pendente/finalizado)
          if (item.dentes && item.situacao) {
            const denteFace = item.dentes;
            if (!historicoVisual[denteFace] || item.situacao === "2") {
              historicoVisual[denteFace] = item.situacao;
            }
          }

          // 🔥 Processar CONDIÇÕES (A, E, C, R, etc.) - pegar a MAIS RECENTE
          if (item.dentes && item.condicao) {
            const numeroMatch = item.dentes.match(/\d+/);
            const numeroDente = numeroMatch ? numeroMatch[0] : null;

            if (numeroDente) {
              // Guardar a condição mais recente para cada dente
              // (considerando data_realizacao ou data_cadastro)
              const dataItem = new Date(
                item.data_realizacao || item.data_cadastro,
              );
              const condicaoExistente = condicoesVisualCompleto[numeroDente];

              if (!condicaoExistente || dataItem > condicaoExistente.data) {
                condicoesVisualCompleto[numeroDente] = {
                  condicao: item.condicao,
                  data: dataItem,
                  denteFace: item.dentes,
                };
              }
            }
          }
        });

        // Converter para o formato simples { numeroDente: condicao }
        const condicoesVisualSimples = {};
        Object.keys(condicoesVisualCompleto).forEach((numeroDente) => {
          condicoesVisualSimples[numeroDente] =
            condicoesVisualCompleto[numeroDente].condicao;
        });

        setHistoricoVisualMap(historicoVisual);
        setCondicoesVisualHistorico(condicoesVisualSimples); // 🔥 NOVO STATE

        // 🔥 3. Se temos um agendamento específico, carregar o histórico dele separadamente
        if (agendamentoIdAtual) {
          const itensAgendamentoAtual = data.data.filter(
            (item) => item.agendamento_id === agendamentoIdAtual,
          );

          const historicoAtual = {};
          const condicoesAtuais = {};

          itensAgendamentoAtual.forEach((item) => {
            if (item.dentes && item.situacao) {
              const denteFace = item.dentes;
              if (!historicoAtual[denteFace] || item.situacao === "2") {
                historicoAtual[denteFace] = item.situacao;
              }
            }

            if (item.dentes && item.condicao) {
              const numeroMatch = item.dentes.match(/\d+/);
              const numeroDente = numeroMatch ? numeroMatch[0] : null;
              if (numeroDente) {
                condicoesAtuais[numeroDente] = item.condicao;
              }
            }
          });

          setHistoricoAgendamentoAtual(historicoAtual);
          setCondicoesDentes(condicoesAtuais);
        } else {
          setHistoricoAgendamentoAtual({});
          setCondicoesDentes({});
        }

        // Processar observações únicas
        const observacoesUnicas = new Map();
        data.data.forEach((item) => {
          if (
            item.procedimento_observacoes &&
            item.procedimento_observacoes.trim() !== ""
          ) {
            if (!observacoesUnicas.has(item.procedimento_observacoes)) {
              observacoesUnicas.set(item.procedimento_observacoes, {
                texto: item.procedimento_observacoes,
                data: item.data_realizacao,
                agendamento_id: item.agendamento_id,
              });
            }
          }
        });

        const listaObservacoesUnicas = Array.from(observacoesUnicas.values());
        setObservacoesExistentes(listaObservacoesUnicas);
      } else {
        throw new Error(data.message || "Erro na estrutura dos dados");
      }
    } catch (error) {
      console.error("❌ Erro ao carregar dados:", error);
      setError(error.message);
      setPacienteEscolhido([]);
      setHistoricoVisualMap({});
      setCondicoesVisualHistorico({}); // 🔥 Resetar
      setHistoricoAgendamentoAtual({});
      setCondicoesDentes({});
      setObservacoesExistentes([]);
    } finally {
      setLoading(false);
    }
  };
  const fetchDadosFicha = async (pacienteId) => {
    /* manter original */
  };
  const buscarCaixaPorAgendamento = async (agendamento_id) => {
    setLoading(true);
    setError(null);

    try {
      if (!user?.token) {
        throw new Error("Usuário não autenticado. Faça login novamente.");
      }

      const response = await fetch(
        `https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/caixas/agendamento/${agendamento_id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Erro ao buscar caixa do agendamento");
      }

      const result = await response.json();

      if (result.success && result.data) {
        const dadosFormatados = {
          ...result.data,
          servico_principal: result.data.servico_principal || [],
          servicos_adicionais: result.data.servicos_adicionais || [],
          formas_pagamento_disponiveis:
            result.data.formas_pagamento_disponiveis || [],
        };

        setPagamentoSelecionado(dadosFormatados);
        setError(null);
        return dadosFormatados;
      } else {
        setError("Nenhum caixa encontrado para este agendamento.");
        return null;
      }
    } catch (error) {
      setError(error.message);
      setPagamentoSelecionado(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================================
  // FUNÇÕES DE FILTROS (mantidas iguais)
  // ==========================================================================
  const filtrarProfissionaisPorEmpresa = (empresaId) => {
    /* manter original */
  };
  const filtrarDatasPorProfissional = (profissionalId) => {
    /* manter original */
  };
  const filtrarHorariosPorProfissionalEData = (profissionalId, data) => {
    /* manter original */
  };
  const filtrarServicosPorProfissional = (profissionalId) => {
    /* manter original */
  };

  // ==========================================================================
  // FUNÇÕES DE AÇÕES (mantidas iguais)
  // ==========================================================================
  const cancelarAgenda = async (agendamentoId, pacienteNome) => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/agendamentos/${agendamentoId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status_reserva: "cancelada",
            status: "cancelado",
          }),
        },
      );

      const data = await response.json();

      if (data.success) {
        setAgendamentos((prev) =>
          prev.filter((item) => item.id !== agendamentoId),
        );
        showSuccessMessage(
          `Agendamento de ${pacienteNome} cancelado com sucesso!`,
        );
      } else {
        throw new Error(data.message || "Erro ao cancelar reserva");
      }
    } catch (error) {
      console.error("Erro:", error);
      showErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };
  const handleAdicionarAgenda = async () => {
    /* manter original */
  };
  const handleAgendarRetorno = async () => {
    /* manter original */
  };
  const handleEditar = async () => {
    /* manter original */
  };
  const handleFinalizarAtendimento = async () => {
    /* manter original */
  };

  // ==========================================================================
  // FUNÇÕES DE SERVIÇOS E DENTES (mantidas iguais)
  // ==========================================================================
  const handleAdicionarServico = () => {
    if (!servicoSelecionadoId) {
      alert("Selecione um serviço primeiro!");
      return;
    }

    setLoading(true);

    const servicoCompleto = servicosFiltrados.find(
      (s) => s.value === servicoSelecionadoId,
    );

    if (!servicoCompleto) {
      alert("Serviço não encontrado!");
      setLoading(false);
      return;
    }

    const novoServico = {
      id: servicoCompleto.value,
      descricao: servicoCompleto.nome,
      duracao: servicoCompleto.duracao,
      valor: servicoCompleto.valor,
      dataAdicao: new Date().toISOString(),
    };

    setServicosSelecionados([...servicosSelecionados, novoServico]);
    setServicoSelecionadoId(null);
    setLoading(false);
  };
  const handleRemoverServico = (id) => {
    const novosServicos = servicosSelecionados.filter((serv) => serv.id !== id);
    setServicosSelecionados(novosServicos);
  };
  const calcularTotalComOriginal = () => {
    const valorOriginal = parseFloat(reservaEditando?.servico_valor || 0);
    const valorAdicionais = servicosSelecionados.reduce((total, servico) => {
      return total + parseFloat(servico.valor || 0);
    }, 0);
    return valorOriginal + valorAdicionais;
  };
  const handleDenteClick = useCallback(
    (denteNumber) => {
      const situacaoHistorico = historicoVisualMap?.[denteNumber];
      const isPendenteHistorico = situacaoHistorico === 1;

      const isInfantil =
        denteNumber.includes("55") ||
        denteNumber.includes("54") ||
        denteNumber.includes("53") ||
        denteNumber.includes("52") ||
        denteNumber.includes("51") ||
        denteNumber.includes("61") ||
        denteNumber.includes("62") ||
        denteNumber.includes("63") ||
        denteNumber.includes("64") ||
        denteNumber.includes("65") ||
        denteNumber.includes("85") ||
        denteNumber.includes("84") ||
        denteNumber.includes("83") ||
        denteNumber.includes("82") ||
        denteNumber.includes("81") ||
        denteNumber.includes("71") ||
        denteNumber.includes("72") ||
        denteNumber.includes("73") ||
        denteNumber.includes("74") ||
        denteNumber.includes("75");

      if (!isInfantil) {
        setSelectedDentesAdulto((prev) => {
          const denteExistente = prev.find((d) => d.numero === denteNumber);

          if (!denteExistente) {
            // Se é pendente do histórico, já começa como "finalizando" (situacao 2)
            const situacaoInicial = isPendenteHistorico ? 2 : 1;
            return [
              ...prev,
              { numero: denteNumber, situacao: situacaoInicial },
            ];
          } else {
            if (denteExistente.situacao === 1) {
              return prev.map((d) =>
                d.numero === denteNumber ? { ...d, situacao: 2 } : d,
              );
            } else if (denteExistente.situacao === 2) {
              return prev.filter((d) => d.numero !== denteNumber);
            }
          }
          return prev;
        });
      } else {
        // Mesma lógica para infantil...
        setSelectedDentesInfantil((prev) => {
          const denteExistente = prev.find((d) => d.numero === denteNumber);

          if (!denteExistente) {
            const situacaoInicial = isPendenteHistorico ? 2 : 1;
            return [
              ...prev,
              { numero: denteNumber, situacao: situacaoInicial },
            ];
          } else {
            if (denteExistente.situacao === 1) {
              return prev.map((d) =>
                d.numero === denteNumber ? { ...d, situacao: 2 } : d,
              );
            } else if (denteExistente.situacao === 2) {
              return prev.filter((d) => d.numero !== denteNumber);
            }
          }
          return prev;
        });
      }
    },
    [historicoVisualMap],
  );
  const handleDescricaoChange = (e) => {
    setDescricaoAtual(e.target.value);
  };
  const handleAdicionarDescricao = () => {
    if (!descricaoAtual.trim()) return;

    // Pega dentes de AMBOS os arrays (adulto e infantil)
    const dentesSelecionados = [
      ...selectedDentesAdulto,
      ...selectedDentesInfantil,
    ];

    if (dentesSelecionados.length === 0) return;

    const novaDescricao = {
      id: Date.now(),
      dentes: dentesSelecionados
        .map(
          (d) =>
            `${d.numero} (${d.situacao === 1 ? "Pendente" : "Finalizado"})`,
        )
        .join(", "),
      descricao: descricaoAtual.trim(),
      dataAdicao: new Date().toISOString(),
      dentesArray: dentesSelecionados.map((d) => d.numero),
      situacoes: dentesSelecionados.reduce((acc, d) => {
        acc[d.numero] = d.situacao;
        return acc;
      }, {}),
    };

    setDescricoesDentarias([...descricoesDentarias, novaDescricao]);
    setDescricaoAtual("");

    // Guarda os dentes de AMBOS os tipos com suas situações
    const novosDentesProcessados = [...dentesProcessados];
    dentesSelecionados.forEach((dente) => {
      const existe = novosDentesProcessados.find(
        (d) => d.numero === dente.numero,
      );
      if (!existe) {
        novosDentesProcessados.push({
          numero: dente.numero,
          situacao: dente.situacao,
        });
      }
    });
    setDentesProcessados(novosDentesProcessados);

    // Limpa AMBOS os arrays
    setSelectedDentesAdulto([]);
    setSelectedDentesInfantil([]);
  };
  const handleRemoverDescricao = (id) => {
    setDescricoesDentarias(
      descricoesDentarias.filter((item) => item.id !== id),
    );
  };
  const limparOdontograma = () => {
    setSelectedDentesAdulto([]);
    setSelectedDentesInfantil([]);
    setDentesProcessados([]);
    setCondicoesDentes({});
    setDescricoesDentarias([]);
    setDescricaoAtual("");
    setObservacoes("");
    setQueixas("");
    setServicosSelecionados([]);
    setServicoSelecionadoId(null);
    setValorTotal("");
  };
  const handleCondicaoChange = (numeroDente, condicao) => {
    // Se condicao for null, é porque clicou para selecionar uma condição
    if (condicao === null) {
      setDenteSelecionadoParaCondicao(numeroDente);
      setVisibleCondicaoDialog(true);
    } else {
      // Se veio com valor, atualiza a condição
      setCondicoesDentes((prev) => ({
        ...prev,
        [numeroDente]: condicao,
      }));
    }
  };
  const handleCondicaoSelecionada = (condicao) => {
    if (denteSelecionadoParaCondicao) {
      setCondicoesDentes((prev) => {
        const novo = {
          ...prev,
          [denteSelecionadoParaCondicao]: condicao.codigo,
        };
        return novo;
      });

      showSuccessMessage(
        `Condição "${condicao.codigo} - ${condicao.descricao}" aplicada ao dente ${denteSelecionadoParaCondicao}`,
      );
      setDenteSelecionadoParaCondicao(null);
    }
  };
  const handleCondicaoClear = () => {
    if (denteSelecionadoParaCondicao) {
      // Remove a condição do estado
      setCondicoesDentes((prev) => {
        const newCondicoes = { ...prev };
        delete newCondicoes[denteSelecionadoParaCondicao];
        return newCondicoes;
      });

      // Mostrar mensagem de sucesso
      showSuccessMessage(
        `Condição removida do dente ${denteSelecionadoParaCondicao}`,
        false,
      );

      // Limpar o dente selecionado
      setDenteSelecionadoParaCondicao(null);
    }
  };

  // ==========================================================================
  // FUNÇÕES DO CALENDÁRIO (mantidas iguais)
  // ==========================================================================
  const verificarReservasNoDia = (data) => {
    if (!agendamentos || agendamentos.length === 0) return false;

    const dataFormatada = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}-${String(data.getDate()).padStart(2, "0")}`;

    return agendamentos.some(
      (reserva) => reserva.data_atendimento?.split("T")[0] === dataFormatada,
    );
  };
  const gerarDiasCalendario = () => {
    const ano = mesCalendario.getFullYear();
    const mes = mesCalendario.getMonth();

    const primeiroDia = new Date(ano, mes, 1);
    const ultimoDia = new Date(ano, mes + 1, 0);
    const diaSemanaInicio = primeiroDia.getDay();

    const dias = [];

    for (let i = diaSemanaInicio - 1; i >= 0; i--) {
      const data = new Date(ano, mes, -i);
      dias.push({
        data,
        disponivel: true,
        selecionado: false,
        possuiReserva: verificarReservasNoDia(data),
        outroMes: true,
      });
    }

    for (let dia = 1; dia <= ultimoDia.getDate(); dia++) {
      const data = new Date(ano, mes, dia);

      const dataFormatada = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}-${String(data.getDate()).padStart(2, "0")}`;
      const diaSelecionadoFormatado = `${diaSelecionado.getFullYear()}-${String(diaSelecionado.getMonth() + 1).padStart(2, "0")}-${String(diaSelecionado.getDate()).padStart(2, "0")}`;

      const selecionado = diaSelecionadoFormatado === dataFormatada;
      const possuiReserva = verificarReservasNoDia(data);

      dias.push({
        data,
        disponivel: true,
        selecionado,
        possuiReserva,
        outroMes: false,
      });
    }

    const totalCells = 42;
    while (dias.length < totalCells) {
      const data = new Date(
        ano,
        mes + 1,
        dias.length - ultimoDia.getDate() - diaSemanaInicio + 1,
      );
      dias.push({
        data,
        disponivel: true,
        selecionado: false,
        possuiReserva: verificarReservasNoDia(data),
        outroMes: true,
      });
    }

    return dias;
  };
  const handleSelecionarDia = (dia) => {
    if (!dia.disponivel) return;

    setDiaSelecionado(new Date(dia.data));
    setDiaParaNovoAgendamento({
      data: new Date(dia.data),
      disponivel: dia.disponivel,
      selecionado: true,
      possuiReserva: dia.possuiReserva,
      outroMes: dia.outroMes,
    });
  };
  const handleMudarMes = (direcao) => {
    const novoMes = new Date(mesCalendario);
    if (direcao === "proximo") {
      novoMes.setMonth(novoMes.getMonth() + 1);
    } else {
      novoMes.setMonth(novoMes.getMonth() - 1);
    }
    setMesCalendario(novoMes);
  };

  // ==========================================================================
  // FUNÇÕES DE UTILIDADE (mantidas iguais)
  // ==========================================================================
  const resetarCampos = (preservarData = false, preservarReserva = false) => {
    /* manter original */
  };
  const resetarCamposPagamento = () => {
    setPagamentosAdicionados([]);
    setFormaPagamentoSelecionada(null);
    setAcrescimo("");
    setDesconto("");
    setMostrarAjustes(false);
  };
  const formatarData = (data) => {
    return new Date(data + "T00:00:00").toLocaleDateString("pt-BR");
  };
  const formatarHorario = (horario) => {
    return horario;
  };
  const showSuccessMessage = (
    message,
    showCaixaConfirmation = false,
    agendamentoId = null,
  ) => {
    setDialogMessage(message);
    setShowSuccessDialog(true);

    if (showCaixaConfirmation) {
      setCaixaConfirmationSuccess(true);
      agendamentoIdRef.current = agendamentoId;
    }
  };
  const showErrorMessage = (message) => {
    setDialogMessage(message);
    setShowErrorDialog(true);
  };
  const alternarDetalhes = (id) => {
    /* manter original */
  };
  const irParaDiaAnterior = () => {
    const novoDia = new Date(diaSelecionado);
    novoDia.setDate(novoDia.getDate() - 1);
    setDiaSelecionado(novoDia);
  };
  const irParaHoje = () => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    setDiaSelecionado(hoje);
  };
  const irParaProximoDia = () => {
    const novoDia = new Date(diaSelecionado);
    novoDia.setDate(novoDia.getDate() + 1);
    setDiaSelecionado(novoDia);
  };
  // Função para abrir confirmação de cancelamento
  const openCancelaConfirm = (id) => {
    setItemCancela(id);
    setShowConfirmDialog(true);
  };
  // Função para abrir agendamento com data
  const handleAbrirAgendamentoComData = () => {
    if (!diaParaNovoAgendamento || !diaParaNovoAgendamento.data) {
      showErrorMessage("Selecione um dia no calendário primeiro");
      return;
    }

    const dataObj = diaParaNovoAgendamento.data;
    const ano = dataObj.getFullYear();
    const mes = String(dataObj.getMonth() + 1).padStart(2, "0");
    const dia = String(dataObj.getDate()).padStart(2, "0");
    const dataFormatada = `${ano}-${mes}-${dia}`;

    // Limpar campos de agendamento
    setEmpresa("");
    setProfissional("");
    setHora("");
    setServico("");
    setHorariosDisponiveis([]);

    // Limpar dados do paciente
    setNovoPaciente({
      nome: "",
      cpf: "",
      telefone: "",
      email: "",
    });

    // Buscar dados
    fetchTodosDados();

    // Seta a data e o flag
    setData(dataFormatada);
    setDataVeioDoCalendario(true);
    setMostrarCalendario(false);

    // Abre o diálogo APENAS UMA VEZ
    setVisibleAdicionar(true);
  };
  const handleSelecionarPaciente = () => {
    /* manter original */
  };
  // Função para o switch
  const handleSwitchChange = (e) => {
    const newValue = e.value;
    setChecked(newValue);
    fetchAgendamentos(newValue);
  };

  // ==========================================================================
  // EFFECTS
  // ==========================================================================
  useEffect(() => {
    fetchAgendamentos();
  }, []);
  useEffect(() => {
    if (pacientes.length > 0) {
      let filtrados = [...pacientes];
      if (pesquisarNome.trim() !== "") {
        filtrados = filtrados.filter((paciente) =>
          paciente.nome?.toLowerCase().includes(pesquisarNome.toLowerCase()),
        );
      }
      if (pesquisarCpf.trim() !== "") {
        const cpfPesquisa = pesquisarCpf.replace(/[^\d]/g, "");
        filtrados = filtrados.filter((paciente) => {
          const cpfPaciente = paciente.cpf?.replace(/[^\d]/g, "") || "";
          return cpfPaciente.includes(cpfPesquisa);
        });
      }
      setPacientesFiltrados(filtrados);
    } else {
      setPacientesFiltrados([]);
    }
  }, [pesquisarNome, pesquisarCpf, pacientes]);

  useEffect(() => {
    if (profissional && data) {
      filtrarHorariosPorProfissionalEData(profissional, data);
    }
  }, [profissional, data, dataVeioDoCalendario]);

  useEffect(() => {
    if (visibleAdicionar && dataVeioDoCalendario && data) {
      const dataExiste = datasDisponiveis.some((d) => d.value === data);
      if (!dataExiste) {
        const dataFormatada = {
          label: new Date(data + "T00:00:00").toLocaleDateString("pt-BR"),
          value: data,
        };
        setDatasDisponiveis((prev) =>
          [...prev, dataFormatada].sort((a, b) =>
            a.value.localeCompare(b.value),
          ),
        );
      }
    }
  }, [visibleAdicionar, dataVeioDoCalendario, data]);

  useEffect(() => {
    if (dialogFinalizar) {
      const total = calcularTotalComOriginal();
      if (total) {
        setValorTotal(total.toFixed(2));
      }
    }
  }, [dialogFinalizar, servicosSelecionados]);

  // ==========================================================================
  // AGENDAMENTOS FILTRADOS
  // ==========================================================================
  const agendamentosFiltrados = useMemo(() => {
    return agendamentos.filter((agenda) => {
      const dataAgendamento = agenda.data_atendimento?.split("T")[0];
      const dataSelecionada = `${diaSelecionado.getFullYear()}-${String(
        diaSelecionado.getMonth() + 1,
      ).padStart(2, "0")}-${String(diaSelecionado.getDate()).padStart(2, "0")}`;
      const dataMatch = dataAgendamento === dataSelecionada;
      const nomeMatch =
        !filtroNome ||
        agenda.paciente_nome?.toLowerCase().includes(filtroNome.toLowerCase());
      const cpfMatch =
        !filtroCpf ||
        agenda.paciente_cpf?.toLowerCase().includes(filtroCpf.toLowerCase());
      const telefoneMatch =
        !filtroTelefone ||
        agenda.paciente_telefone
          ?.toLowerCase()
          .includes(filtroTelefone.toLowerCase());
      const emailMatch =
        !filtroEmail ||
        agenda.paciente_email
          ?.toLowerCase()
          .includes(filtroEmail.toLowerCase());
      return dataMatch && nomeMatch && cpfMatch && telefoneMatch && emailMatch;
    });
  }, [
    agendamentos,
    diaSelecionado,
    filtroNome,
    filtroCpf,
    filtroTelefone,
    filtroEmail,
  ]);

  // ==========================================================================
  // RENDER PRINCIPAL
  // ==========================================================================
  return (
    <div
      style={{
        fontFamily: "'Rajdhani', sans-serif",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      {/* ======================================================================
          DIÁLOGOS DE CONFIRMAÇÃO (COMPONENTES SEPARADOS)
      ====================================================================== */}
      <DialogSucesso
        visible={showSuccessDialog}
        onHide={() => {
          setShowSuccessDialog(false);
          if (caixaConfirmationSuccess && agendamentoIdRef.current) {
            setShowConfirmCaixa(true);
          }
        }}
        message={dialogMessage}
        onConfirm={() => {
          setShowSuccessDialog(false);
          if (caixaConfirmationSuccess && agendamentoIdRef.current) {
            setShowConfirmCaixa(true);
          }
        }}
      />
      <DialogErro
        visible={showErrorDialog}
        onHide={() => setShowErrorDialog(false)}
        message={dialogMessage}
      />
      <DialogCancelar
        visible={showConfirmDialog}
        onHide={() => setShowConfirmDialog(false)}
        onConfirm={() => {
          if (itemCancela) {
            const item = agendamentosFiltrados.find(
              (ag) => ag.id === itemCancela,
            );
            if (item) {
              cancelarAgenda(item.id, item.paciente_nome);
            }
          }
          setShowConfirmDialog(false);
        }}
      />
      {/* Dialog de Finalizar após criação */}
      <DialogConfirmarAtendimento
        visible={showConfirmFinalizarAposCriacao}
        onHide={() => {
          setShowConfirmFinalizarAposCriacao(false);
          setAgendamentoCriadoId(null);
          setReservaEditando(null);
        }}
        onConfirm={async () => {
          setShowConfirmFinalizarAposCriacao(false);

          setTimeout(async () => {
            if (reservaEditando?.paciente_id) {
              setAgendamentoAtualId(reservaEditando.id);
              await buscarFichaPaciente(
                reservaEditando.paciente_id,
                reservaEditando.id,
              );
              setDialogFinalizar(true);
            }
          }, 100);
        }}
      />
      {/* Dialog Direto para o Caixa */}
      <DialogConfirmarCaixa
        visible={showConfirmCaixa}
        onHide={() => {
          setShowConfirmCaixa(false);
          agendamentoIdRef.current = null;
        }}
        onConfirm={async () => {
          setShowConfirmCaixa(false);

          let caixaEncontrado = null;

          // Verificar se tem dados completos salvos
          if (
            window.caixaCompleto &&
            window.caixaCompleto.agendamento_id == agendamentoIdRef.current
          ) {
            caixaEncontrado = window.caixaCompleto;
            window.caixaCompleto = null;
          } else {
            caixaEncontrado = await buscarCaixaPorAgendamento(
              agendamentoIdRef.current,
            );
          }

          if (caixaEncontrado) {
            setPagamentoSelecionado(caixaEncontrado);
            setVisibleCaixa(true);
          } else {
            showErrorMessage("Caixa não encontrado para este agendamento.");
          }
          agendamentoIdRef.current = null;
        }}
      />
      {/* Dialog de Seleção de Condição */}
      <CondicaoSelector
        visible={visibleCondicaoDialog}
        onHide={() => {
          setVisibleCondicaoDialog(false);
          setDenteSelecionadoParaCondicao(null);
        }}
        onSelect={handleCondicaoSelecionada}
        onClear={handleCondicaoClear}
        denteNumero={denteSelecionadoParaCondicao}
        condicaoAtual={
          condicoesDentes?.[denteSelecionadoParaCondicao]
            ? {
                codigo: condicoesDentes[denteSelecionadoParaCondicao],
                descricao: "",
              }
            : null
        }
      />
      {/* Dialog do Caixa - FecharCaixa */}
      <FecharCaixa
        visible={visibleCaixa}
        onHide={() => {
          setVisibleCaixa(false);
          resetarCamposPagamento();
        }}
        onSuccess={(message, reciboData) => {
          showSuccessMessage(message);
          if (reciboData) {
            // Armazenar dados do recibo para impressão se necessário
            window.pendenteImpressao = reciboData;
          }
        }}
        onError={showErrorMessage}
        pagamentoSelecionado={pagamentoSelecionado}
        onFechado={async () => {
          await fetchAgendamentos(checked);
          setVisibleCaixa(false);
          resetarCamposPagamento();
        }}
      />
      {/* Dialog do Histórico */}
      <DialogHistoricoPaciente
        visible={visibleHistorico}
        onHide={() => setVisibleHistorico(false)}
        pacienteEscolhido={pacienteEscolhido}
        reservaEditando={reservaEditando}
        itensExpandidos={itensExpandidos}
        alternarDetalhes={alternarDetalhes}
      />
      {/* Dialog Nova Agenda */}
      <DialogNovaAgenda
        visible={visibleAdicionar}
        onHide={() => {
          if (!loading) {
            resetarCampos();
            setVisibleAdicionar(false);
          }
        }}
        // Estados do formulário
        novoPaciente={novoPaciente}
        setNovoPaciente={setNovoPaciente}
        empresa={empresa}
        setEmpresa={setEmpresa}
        profissional={profissional}
        setProfissional={setProfissional}
        data={data}
        setData={setData}
        hora={hora}
        setHora={setHora}
        servico={servico}
        setServico={setServico}
        // Dados e filtros
        dadosCompletos={dadosCompletos}
        profissionaisFiltrados={profissionaisFiltrados}
        horariosDisponiveis={horariosDisponiveis}
        setHorariosDisponiveis={setHorariosDisponiveis}
        servicosFiltrados={servicosFiltrados}
        // Funções de filtro
        filtrarProfissionaisPorEmpresa={filtrarProfissionaisPorEmpresa}
        filtrarDatasPorProfissional={filtrarDatasPorProfissional}
        filtrarServicosPorProfissional={filtrarServicosPorProfissional}
        // Callbacks
        setReservaEditando={setReservaEditando}
        setAgendamentoCriadoId={setAgendamentoCriadoId}
        setShowConfirmFinalizarAposCriacao={setShowConfirmFinalizarAposCriacao}
        fetchAgendamentos={fetchAgendamentos}
        fetchTodosDados={fetchTodosDados}
        showSuccessMessage={showSuccessMessage}
        showErrorMessage={showErrorMessage}
        resetarCampos={resetarCampos}
        setVisiblePesquisar={setVisiblePesquisar}
        checked={checked}
        dataVeioDoCalendario={dataVeioDoCalendario}
      />
      {/* Dialog Pesquisar Paciente */}
      <DialogPesquisarPaciente
        visible={visiblePesquisar}
        onHide={() => {
          if (!loading) {
            setVisiblePesquisar(false);
          }
        }}
        onPacienteSelecionado={(paciente) => {
          // Preencher os dados do paciente no formulário
          setNovoPaciente({
            nome: paciente.nome || "",
            cpf: paciente.cpf || "",
            telefone: paciente.telefone || "",
            email: paciente.email || "",
          });
          setVisiblePesquisar(false);
        }}
        showErrorMessage={showErrorMessage}
      />
      {/* Dialog Editar Agenda */}
      <DialogEditarAgenda
        visible={visibleEditar}
        onHide={() => {
          if (!loading) {
            resetarCampos();
            setVisibleEditar(false);
          }
        }}
        reservaEditando={reservaEditando}
        empresa={empresa}
        setEmpresa={setEmpresa}
        profissional={profissional}
        setProfissional={setProfissional}
        data={data}
        setData={setData}
        hora={hora}
        setHora={setHora}
        servico={servico}
        setServico={setServico}
        dadosCompletos={dadosCompletos}
        profissionaisFiltrados={profissionaisFiltrados}
        horariosDisponiveis={horariosDisponiveis}
        setHorariosDisponiveis={setHorariosDisponiveis}
        servicosFiltrados={servicosFiltrados}
        filtrarProfissionaisPorEmpresa={filtrarProfissionaisPorEmpresa}
        filtrarServicosPorProfissional={filtrarServicosPorProfissional}
        filtrarHorariosPorProfissionalEData={
          filtrarHorariosPorProfissionalEData
        }
        fetchAgendamentos={fetchAgendamentos}
        fetchTodosDados={fetchTodosDados}
        showSuccessMessage={showSuccessMessage}
        showErrorMessage={showErrorMessage}
        resetarCampos={resetarCampos}
        setReservaEditando={setReservaEditando}
        setVisibleEditar={setVisibleEditar}
        setShowConfirmFinalizarAposCriacao={setShowConfirmFinalizarAposCriacao}
        checked={checked}
      />
      {/* Dialog Agendar Retorno */}
      <DialogAgendarRetorno
        visible={visibleRetorno}
        onHide={() => {
          if (!loading) {
            setData("");
            setHora("");
            setServico("");
            setVisibleRetorno(false);
          }
        }}
        reservaEditando={reservaEditando}
        empresa={empresa}
        setEmpresa={setEmpresa}
        profissional={profissional}
        setProfissional={setProfissional}
        data={data}
        setData={setData}
        hora={hora}
        setHora={setHora}
        servico={servico}
        setServico={setServico}
        dadosCompletos={dadosCompletos}
        profissionaisFiltrados={profissionaisFiltrados}
        horariosDisponiveis={horariosDisponiveis}
        setHorariosDisponiveis={setHorariosDisponiveis}
        servicosFiltrados={servicosFiltrados}
        filtrarProfissionaisPorEmpresa={filtrarProfissionaisPorEmpresa}
        filtrarServicosPorProfissional={filtrarServicosPorProfissional}
        showSuccessMessage={showSuccessMessage}
        showErrorMessage={showErrorMessage}
        fetchAgendamentos={fetchAgendamentos}
        fetchTodosDados={fetchTodosDados}
        checked={checked}
      />
      {/* Dialog Ficha Paciente */}
      <DialogFichaPaciente
        visible={visibleFicha}
        onHide={() => setVisibleFicha(false)}
        fichaData={fichaData}
        reservaEditando={reservaEditando}
      />
      {/* Dialog Finalizar Atendimento */}
      <DialogRealizarAtendimento
        visible={dialogFinalizar}
        onHide={() => {
          if (!loading) {
            resetarCampos();
            setDialogFinalizar(false);
            setServicosSelecionados([]);
            setSelectedDentesAdulto([]);
            setSelectedDentesInfantil([]);
            setServicoSelecionadoId(null);
            setCondicoesDentes({});
            setDescricoesDentarias([]);
            setDescricaoAtual("");
            setObservacoes("");
            setQueixas("");
            setDentesProcessados([]);
            resetarCamposPagamento();
          }
        }}
        reservaEditando={reservaEditando}
        pacienteEscolhido={pacienteEscolhido}
        queixas={queixas}
        setQueixas={setQueixas}
        observacoes={observacoes}
        setObservacoes={setObservacoes}
        observacoesExistentes={observacoesExistentes}
        mostrarAdulto={mostrarAdulto}
        setMostrarAdulto={setMostrarAdulto}
        mostrarInfantil={mostrarInfantil}
        setMostrarInfantil={setMostrarInfantil}
        selectedDentesAdulto={selectedDentesAdulto}
        setSelectedDentesAdulto={setSelectedDentesAdulto}
        selectedDentesInfantil={selectedDentesInfantil}
        setSelectedDentesInfantil={setSelectedDentesInfantil}
        handleDenteClick={handleDenteClick}
        handleCondicaoChange={handleCondicaoChange}
        historicoVisualMap={historicoVisualMap}
        condicoesVisualHistorico={condicoesVisualHistorico}
        condicoesDentes={condicoesDentes}
        setCondicoesDentes={setCondicoesDentes}
        dentesProcessados={dentesProcessados}
        setDentesProcessados={setDentesProcessados}
        descricoesDentarias={descricoesDentarias}
        setDescricoesDentarias={setDescricoesDentarias}
        servicosSelecionados={servicosSelecionados}
        setServicosSelecionados={setServicosSelecionados}
        servicoSelecionadoId={servicoSelecionadoId}
        setServicoSelecionadoId={setServicoSelecionadoId}
        servicosFiltrados={servicosFiltrados}
        valorTotal={valorTotal}
        setValorTotal={setValorTotal}
        calcularTotalComOriginal={calcularTotalComOriginal}
        DentesAdultoMemo={DentesAdultoMemo}
        DentesInfantilMemo={DentesInfantilMemo}
        formatarData={formatarData}
        formatarHorario={formatarHorario}
        fetchAgendamentos={fetchAgendamentos}
        fetchTodosDados={fetchTodosDados}
        showSuccessMessage={showSuccessMessage}
        showErrorMessage={showErrorMessage}
        resetarCampos={resetarCampos}
        setReservaEditando={setReservaEditando}
        setDialogFinalizar={setDialogFinalizar}
      />

      {/* ======================================================================
          BARRA SUPERIOR VERMELHA
      ====================================================================== */}
      <div
        style={{
          backgroundColor: "#B83732",
          height: "40px",
          minHeight: "40px",
          width: "100%",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 20px",
          fontWeight: "bold",
          position: "fixed",
          zIndex: 1,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <div></div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Button
            icon="pi pi-refresh"
            className="icon-button-titulo"
            onClick={() => {
              fetchAgendamentos();
              setChecked(false);
              irParaHoje();
              resetarCamposPagamento();
            }}
            style={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              color: "#ffffff",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            outlined
          />
          <Button
            icon={visualizacaoResumida ? "pi pi-eye" : "pi pi-eye-slash"}
            className="icon-button-titulo"
            onClick={() => setVisualizacaoResumida(!visualizacaoResumida)}
            style={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              color: "#ffffff",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            outlined
          />
          <Button
            icon={mostrarFiltroAgenda ? "pi pi-filter-slash" : "pi pi-filter"}
            className="icon-button-titulo"
            onClick={() => setMostrarFiltroAgenda(!mostrarFiltroAgenda)}
            style={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              color: "#ffffff",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            outlined
          />
          <Button
            icon={
              mostrarCalendario ? "pi pi-calendar-minus" : "pi pi-calendar-plus"
            }
            className="icon-button-titulo"
            onClick={() => setMostrarCalendario(!mostrarCalendario)}
            style={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              color: "#ffffff",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            outlined
          />
          <Button
            icon="pi pi-plus"
            className="icon-button-titulo"
            onClick={() => {
              if (
                user.tipo === "administrator" ||
                user.modulos?.Agenda?.editar
              ) {
                setVisibleAdicionar(true);
                fetchTodosDados();
                resetarCampos();
              }
            }}
            disabled={
              !(user.tipo === "administrator" || user.modulos?.Agenda?.editar)
            }
            style={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              color: "#ffffff",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
              opacity:
                user.tipo === "administrator" || user.modulos?.Agenda?.editar
                  ? 1
                  : 0.4,
              cursor:
                user.tipo === "administrator" || user.modulos?.Agenda?.editar
                  ? "pointer"
                  : "not-allowed",
            }}
            outlined
          />
        </div>
        <div></div>
      </div>
      {/* ======================================================================
          CONTEÚDO PRINCIPAL
      ====================================================================== */}
      <div style={{ flex: 1, padding: "50px 10px" }}>
        <div style={{ borderRadius: "8px", padding: "0 15px" }}>
          {/* Cabeçalho da Agenda */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <FaCalendarCheck
                style={{ color: "#B83732", fontSize: "1.2rem" }}
              />
              <h3 style={{ margin: 0, color: "#424242" }}>AGENDAS</h3>
              {/* Bolinha com quantidade */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "25px",
                  height: "25px",
                  background:
                    "linear-gradient(135deg, #B83732 0%, #D65A55 100%)",
                  borderRadius: "50px",
                  padding: "0 8px",
                  margin: "0 8px",
                  boxShadow: "0 2px 4px rgba(184, 55, 50, 0.2)",
                }}
              >
                <span
                  style={{
                    color: "white",
                    fontSize: "0.85rem",
                    fontWeight: "600",
                    lineHeight: 1,
                  }}
                >
                  {agendamentosFiltrados.length}
                </span>
              </div>
            </div>
            <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
              <Button
                icon="pi pi-chevron-left"
                className="p-button-text p-button-sm"
                onClick={irParaDiaAnterior}
                tooltip="Dia anterior"
                style={{
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  color: "#424242",
                }}
              />
              <h3 style={{ margin: 0, color: "#424242" }}>
                {diaSelecionado.toLocaleDateString("pt-BR")}
              </h3>
              <Button
                icon="pi pi-chevron-right"
                className="p-button-text p-button-sm"
                onClick={irParaProximoDia}
                tooltip="Próximo Dia"
                style={{
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  color: "#424242",
                }}
              />
            </div>
          </div>

          {/* Switch Mostrar Todos */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              marginBottom: "12px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <InputSwitch
                checked={checked}
                onChange={handleSwitchChange}
                style={{ transform: "scale(0.8)" }}
              />
              <span style={{ fontSize: "0.85rem", color: "#4A5568" }}>
                Mostrar todos
              </span>
            </div>
          </div>

          {/* ==================================================================
              CALENDÁRIO VISUAL (simplificado - mantenha seu código original)
          ================================================================== */}
          {mostrarCalendario && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  borderRadius: "8px",
                  padding: "10px 8px",
                  border: "1px solid #dee2e6",
                  backgroundColor: "#fff",
                  width: "95%",
                  maxWidth: "920px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "5px",
                    padding: "0 10px",
                  }}
                >
                  <div></div>

                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                      icon="pi pi-chevron-left"
                      className="p-button-text p-button-sm"
                      onClick={() => handleMudarMes("anterior")}
                      style={{
                        border: "none",
                        outline: "none",
                        boxShadow: "none",
                        color: "#424242",
                        width: "30px",
                        height: "30px",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: "bold",
                        color: "#424242",
                        margin: "0 10px",
                      }}
                    >
                      {mesCalendario
                        .toLocaleDateString("pt-BR", {
                          month: "long",
                          year: "numeric",
                        })
                        .toUpperCase()}
                    </span>
                    <Button
                      icon="pi pi-chevron-right"
                      className="p-button-text p-button-sm"
                      onClick={() => handleMudarMes("proximo")}
                      style={{
                        border: "none",
                        outline: "none",
                        boxShadow: "none",
                        color: "#424242",
                        width: "30px",
                        height: "30px",
                      }}
                    />
                  </div>

                  <div>
                    <Button
                      icon="pi pi-plus"
                      className="p-button-success p-button-sm"
                      onClick={handleAbrirAgendamentoComData}
                      style={{
                        outline: "none",
                        boxShadow: "none",
                        padding: "8px 24px",
                        minWidth: "35px",
                        borderColor: "#dee2e6",
                        color: "white",
                        fontWeight: "500",
                        fontSize: "0.875rem",
                        cursor: diaParaNovoAgendamento
                          ? "pointer"
                          : "not-allowed",
                      }}
                      tooltip={
                        diaParaNovoAgendamento
                          ? `Adicionar agenda ${diaParaNovoAgendamento.data.toLocaleDateString("pt-BR")}`
                          : "Selecione um dia no calendário"
                      }
                      tooltipOptions={{ position: "bottom" }}
                      disabled={!diaParaNovoAgendamento}
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    gap: "2px",
                    marginBottom: "10px",
                  }}
                >
                  {["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"].map(
                    (dia) => (
                      <div
                        key={dia}
                        style={{
                          textAlign: "center",
                          fontWeight: "bold",
                          padding: "5px 2px",
                          backgroundColor: "#e9ecef",
                          borderRadius: "3px",
                          fontSize: "0.8rem",
                          color: "#495057",
                        }}
                      >
                        {dia}
                      </div>
                    ),
                  )}

                  {gerarDiasCalendario().map((dia, index) => {
                    const clicavel =
                      dia.disponivel !== false ||
                      (dia.possuiReserva && dia.disponivel !== false);

                    let backgroundColor = "#ffffff";
                    if (dia.selecionado) backgroundColor = "#fff0f0";
                    else if (dia.outroMes) backgroundColor = "#f8f9fa";
                    else if (dia.possuiReserva) backgroundColor = "#e8f5e9";

                    let color = "#424242";
                    if (dia.outroMes) color = "#adb5bd";
                    else if (dia.selecionado) color = "#b83732";
                    else if (dia.possuiReserva) color = "#28a745";

                    let borderColor = "#dee2e6";
                    if (dia.selecionado) borderColor = "#a8322d";
                    else if (dia.possuiReserva) borderColor = "#28a745";

                    return (
                      <div
                        key={index}
                        onClick={() => clicavel && handleSelecionarDia(dia)}
                        style={{
                          textAlign: "center",
                          padding: "8px 1px",
                          backgroundColor: backgroundColor,
                          borderRadius: "4px",
                          cursor: clicavel ? "pointer" : "not-allowed",
                          border: `1px solid ${borderColor}`,
                          fontWeight: dia.selecionado ? "bold" : "normal",
                          color: color,
                          position: "relative",
                          height: "45px",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          fontSize: "0.85rem",
                          opacity: dia.outroMes ? 0.6 : 1,
                          boxSizing: "border-box",
                        }}
                        className={clicavel ? "day-hover" : ""}
                      >
                        <div
                          style={{
                            fontWeight:
                              dia.data.getDate() === new Date().getDate() &&
                              !dia.outroMes
                                ? "bold"
                                : "normal",
                            fontSize: "0.85rem",
                          }}
                        >
                          {dia.data.getDate()}
                        </div>

                        {dia.possuiReserva && !dia.outroMes && (
                          <div
                            style={{
                              position: "absolute",
                              top: "3px",
                              right: "3px",
                              width: "8px",
                              height: "8px",
                              backgroundColor: "#28a745",
                              borderRadius: "50%",
                            }}
                          />
                        )}

                        {dia.data.getDate() === new Date().getDate() &&
                          dia.data.getMonth() === new Date().getMonth() &&
                          dia.data.getFullYear() === new Date().getFullYear() &&
                          !dia.outroMes && (
                            <div
                              style={{
                                position: "absolute",
                                bottom: "2px",
                                width: "4px",
                                height: "4px",
                                backgroundColor: "#007bff",
                                borderRadius: "50%",
                              }}
                            />
                          )}
                      </div>
                    );
                  })}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "15px",
                    flexWrap: "wrap",
                    fontSize: "0.75rem",
                    paddingTop: "10px",
                    borderTop: "1px solid #e0e0e0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        backgroundColor: "#b83732",
                        borderRadius: "2px",
                      }}
                    ></div>
                    <span style={{ color: "#6c757d" }}>Selecionado</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        backgroundColor: "#28a745",
                        borderRadius: "2px",
                      }}
                    ></div>
                    <span style={{ color: "#6c757d" }}>Com agenda</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        backgroundColor: "#ffffff",
                        border: "1px solid #007bff",
                        borderRadius: "2px",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          bottom: "1px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "4px",
                          height: "4px",
                          backgroundColor: "#007bff",
                          borderRadius: "50%",
                        }}
                      />
                    </div>
                    <span style={{ color: "#6c757d" }}>Hoje</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ==================================================================
              FILTROS DA AGENDA (simplificado)
          ================================================================== */}
          {mostrarFiltroAgenda && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  borderRadius: "8px",
                  padding: "10px 8px",
                  border: "1px solid #dee2e6",
                  backgroundColor: "#fff",
                  width: "95%",
                  maxWidth: "920px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(200px, 1fr))",
                    gap: "10px",
                  }}
                >
                  <div style={{ padding: "10px" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          paddingBottom: "5px",
                          borderBottom: "2px solid #3498DB",
                        }}
                      >
                        <FaUser
                          style={{ color: "#3498DB", fontSize: "0.9rem" }}
                        />
                        <span
                          style={{
                            fontWeight: "600",
                            color: "#2c3e50",
                            fontSize: "0.95rem",
                          }}
                        >
                          Nome
                        </span>
                      </div>
                      <InputText
                        className="p-inputtext-sm"
                        placeholder="Nome do paciente"
                        value={filtroNome}
                        onChange={(e) => setFiltroNome(e.target.value)}
                        style={{
                          outline: "none",
                          boxShadow: "none",
                          width: "100%",
                          height: "35px",
                          fontFamily: "'Rajdhani', sans-serif",
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ padding: "10px" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          paddingBottom: "5px",
                          borderBottom: "2px solid #E67E22",
                        }}
                      >
                        <FaIdCard
                          style={{ color: "#E67E22", fontSize: "0.9rem" }}
                        />
                        <span
                          style={{
                            fontWeight: "600",
                            color: "#2c3e50",
                            fontSize: "0.95rem",
                          }}
                        >
                          CPF
                        </span>
                      </div>
                      <InputText
                        className="p-inputtext-sm"
                        placeholder="CPF do paciente"
                        value={filtroCpf}
                        onChange={(e) => setFiltroCpf(e.target.value)}
                        style={{
                          outline: "none",
                          boxShadow: "none",
                          width: "100%",
                          height: "35px",
                          fontFamily: "'Rajdhani', sans-serif",
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ padding: "10px" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          paddingBottom: "5px",
                          borderBottom: "2px solid #2980b9",
                        }}
                      >
                        <FaPhone
                          style={{ color: "#2980b9", fontSize: "0.9rem" }}
                        />
                        <span
                          style={{
                            fontWeight: "600",
                            color: "#2c3e50",
                            fontSize: "0.95rem",
                          }}
                        >
                          Telefone
                        </span>
                      </div>
                      <InputText
                        className="p-inputtext-sm"
                        placeholder="Telefone do paciente"
                        value={filtroTelefone}
                        onChange={(e) => setFiltroTelefone(e.target.value)}
                        style={{
                          outline: "none",
                          boxShadow: "none",
                          width: "100%",
                          height: "35px",
                          fontFamily: "'Rajdhani', sans-serif",
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ padding: "10px" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          paddingBottom: "5px",
                          borderBottom: "2px solid #d35400",
                        }}
                      >
                        <FaEnvelope
                          style={{ color: "#d35400", fontSize: "0.9rem" }}
                        />
                        <span
                          style={{
                            fontWeight: "600",
                            color: "#2c3e50",
                            fontSize: "0.95rem",
                          }}
                        >
                          Email
                        </span>
                      </div>
                      <InputText
                        className="p-inputtext-sm"
                        placeholder="Email do paciente"
                        value={filtroEmail}
                        onChange={(e) => setFiltroEmail(e.target.value)}
                        style={{
                          outline: "none",
                          boxShadow: "none",
                          width: "100%",
                          height: "35px",
                          fontFamily: "'Rajdhani', sans-serif",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ==================================================================
              LISTA DE AGENDAMENTOS
          ================================================================== */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              padding: "10px 8px",
            }}
          >
            {agendamentosFiltrados.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                  border: "2px dashed #e8e8e8",
                }}
              >
                <FaCalendarCheck
                  style={{
                    fontSize: "3rem",
                    color: "#B83732",
                    marginBottom: "15px",
                    opacity: 0.5,
                  }}
                />
                <h3
                  style={{
                    color: "#2c3e50",
                    margin: "0 0 10px 0",
                    fontSize: "1.2rem",
                    fontWeight: "600",
                  }}
                >
                  Nenhuma agenda encontrada
                </h3>
              </div>
            ) : !visualizacaoResumida ? (
              /* VISUALIZAÇÃO EM LINHA */
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                {agendamentosFiltrados.map((item, idx) => (
                  <div key={idx}>
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #e8e8e8",
                        borderRadius: "10px",
                        padding: "12px 14px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                        flexWrap: "wrap",
                      }}
                    >
                      {/* Coluna principal - informações do paciente */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          minWidth: isMobile ? "180px" : "260px",
                          flex: "1 1 260px",
                        }}
                      >
                        <FaUserCircle
                          style={{
                            fontSize: isMobile ? "1.8rem" : "2.5rem",
                            color: "#D65A55",
                            flexShrink: 0,
                          }}
                        />
                        <div style={{ minWidth: 0 }}>
                          <h3
                            style={{
                              fontSize: "1rem",
                              fontWeight: "600",
                              color: "#2c3e50",
                              margin: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.paciente_nome}
                          </h3>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "5px",
                              alignItems: "center",
                              color: "#64748b",
                              fontSize: "0.9rem",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                marginRight: "6px",
                              }}
                            >
                              <i
                                className="pi pi-phone"
                                style={{ fontSize: "0.7rem" }}
                              />
                              <span>{item.paciente_telefone}</span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                color: "#64748b",
                                fontSize: "0.85rem",
                              }}
                            >
                              <i
                                className="pi pi-tag"
                                style={{ fontSize: "0.7rem" }}
                              />
                              <span>{item.id}</span>
                            </div>
                            {/* Só mostrar Data, Hora e Empresa se NÃO for mobile */}
                            {!isMobile && (
                              <>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    marginRight: "6px",
                                  }}
                                >
                                  <FaCalendarAlt
                                    style={{
                                      fontSize: "0.7rem",
                                      color: "#2ecc71",
                                    }}
                                  />
                                  <span>
                                    {formatarData(item.data_atendimento)}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    marginRight: "6px",
                                  }}
                                >
                                  <FaClock
                                    style={{
                                      fontSize: "0.7rem",
                                      color: "#f39c12",
                                    }}
                                  />
                                  <span>{formatarHorario(item.horario)}</span>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <FaBuilding
                                    style={{
                                      fontSize: "0.7rem",
                                      color: "#3498db",
                                    }}
                                  />
                                  <span>{item.empresa_nome}</span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Área de status e botões */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: "8px",
                          flex: "1 1 320px",
                          marginLeft: "auto",
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "5px",
                            flexWrap: "wrap",
                          }}
                        >
                          {item.status === "realizado" ? (
                            <span
                              style={{
                                backgroundColor: "#d4edda",
                                color: "#155724",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                fontSize: "0.75rem",
                                fontWeight: "600",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <i
                                className="pi pi-check"
                                style={{ fontSize: "0.7rem" }}
                              />
                              ATENDIDO
                            </span>
                          ) : item.status === "cancelado" ? (
                            <span
                              style={{
                                backgroundColor: "#f8d7da",
                                color: "#721c24",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                fontSize: "0.75rem",
                                fontWeight: "600",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <i
                                className="pi pi-times"
                                style={{ fontSize: "0.7rem" }}
                              />
                              CANCELADO
                            </span>
                          ) : (
                            <span
                              style={{
                                backgroundColor: "#fff3cd",
                                color: "#856404",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                fontSize: "0.75rem",
                                fontWeight: "600",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <i
                                className="pi pi-clock"
                                style={{ fontSize: "0.7rem" }}
                              />
                              AGENDADO
                            </span>
                          )}

                          {item.tipo_agendamento === "retorno" && (
                            <span
                              style={{
                                backgroundColor: "#cce5ff",
                                color: "#004085",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                fontSize: "0.75rem",
                                fontWeight: "600",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <i
                                className="pi pi-sync"
                                style={{ fontSize: "0.7rem" }}
                              />
                              RETORNO
                            </span>
                          )}
                        </div>

                        <div style={{ display: "flex", gap: "5px" }}>
                          {item.status !== "realizado" &&
                            item.status !== "cancelado" && (
                              <>
                                <Button
                                  icon="pi pi-check"
                                  onClick={async () => {
                                    setReservaEditando(item);
                                    setAgendamentoAtualId(item.id);
                                    fetchTodosDados();
                                    await buscarFichaPaciente(
                                      item.paciente_id,
                                      item.id,
                                    );
                                    setDialogFinalizar(true);
                                  }}
                                  disabled={
                                    !(
                                      user.tipo === "administrator" ||
                                      user.modulos?.Agenda?.editar
                                    )
                                  }
                                  className="p-button-success p-button-sm"
                                  style={{
                                    outline: "none",
                                    boxShadow: "none",
                                    width: "25px",
                                    height: "25px",
                                    borderColor: "#dee2e6",
                                  }}
                                  tooltip="Realizar atendimento"
                                  tooltipOptions={{ position: "top" }}
                                />
                                <Button
                                  icon="pi pi-times"
                                  onClick={() => {
                                    if (
                                      user.tipo === "administrator" ||
                                      user.modulos?.Agenda?.editar
                                    ) {
                                      openCancelaConfirm(item.id);
                                    }
                                  }}
                                  disabled={
                                    !(
                                      user.tipo === "administrator" ||
                                      user.modulos?.Agenda?.editar
                                    )
                                  }
                                  className="p-button-danger p-button-sm"
                                  style={{
                                    outline: "none",
                                    boxShadow: "none",
                                    width: "25px",
                                    height: "25px",
                                    borderRadius: "6px",
                                  }}
                                  tooltip="Cancelar"
                                  tooltipOptions={{ position: "top" }}
                                />
                              </>
                            )}
                          <Button
                            icon="pi pi-file-edit"
                            onClick={() => {
                              if (
                                (user.tipo === "administrator" ||
                                  user.modulos?.Agenda?.editar) &&
                                item.status === "confirmado"
                              ) {
                                setReservaEditando(item);
                                fetchTodosDados();
                                setEmpresa(item.empresa_id);
                                setProfissional(item.profissional_id);
                                setServico(item.servico_id);
                                setVisibleEditar(true);
                              }
                            }}
                            disabled={
                              !(
                                user.tipo === "administrator" ||
                                user.modulos?.Agenda?.editar
                              ) || item.status !== "confirmado"
                            }
                            className="p-button-outlined p-button-sm"
                            style={{
                              outline: "none",
                              boxShadow: "none",
                              width: "25px",
                              height: "25px",
                              borderColor: "#dee2e6",
                              color: "#495057",
                            }}
                            tooltip="Editar"
                            tooltipOptions={{ position: "top" }}
                          />
                          <Button
                            icon="pi pi-history"
                            onClick={async () => {
                              setReservaEditando(item);
                              await buscarFichaPaciente(item.paciente_id);
                              setVisibleHistorico(true);
                            }}
                            className="p-button-outlined p-button-sm"
                            style={{
                              outline: "none",
                              boxShadow: "none",
                              width: "25px",
                              height: "25px",
                              borderColor: "#dee2e6",
                              color: "#495057",
                            }}
                            tooltip="Histórico"
                            tooltipOptions={{ position: "top" }}
                          />
                          <Button
                            icon="pi pi-sync"
                            onClick={() => {
                              if (
                                user.tipo === "administrator" ||
                                user.modulos?.Agenda?.editar
                              ) {
                                setReservaEditando(item);
                                fetchTodosDados();
                                setEmpresa(item.empresa_id);
                                setProfissional(item.profissional_id);
                                setServico(item.servico_id);
                                setVisibleRetorno(true);
                              }
                            }}
                            disabled={
                              !(
                                user.tipo === "administrator" ||
                                user.modulos?.Agenda?.editar
                              )
                            }
                            className="p-button-outlined p-button-sm"
                            style={{
                              outline: "none",
                              boxShadow: "none",
                              width: "25px",
                              height: "25px",
                              borderColor: "#dee2e6",
                              color: "#495057",
                            }}
                            tooltip="Retorno"
                            tooltipOptions={{ position: "top" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* VISUALIZAÇÃO EM GRADE */
              <div
                className="grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                  gap: "15px",
                  alignItems: "stretch",
                }}
              >
                {agendamentosFiltrados.map((item, idx) => (
                  <div key={idx}>
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #e8e8e8",
                        borderRadius: "10px",
                        padding: "12px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "12px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "5px",
                            flexWrap: "wrap",
                          }}
                        >
                          {item.status === "realizado" ? (
                            <span
                              style={{
                                backgroundColor: "#d4edda",
                                color: "#155724",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                fontSize: "0.85rem",
                                fontWeight: "600",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <i
                                className="pi pi-check"
                                style={{ fontSize: "0.7rem" }}
                              ></i>
                              ATENDIDO
                            </span>
                          ) : item.status === "cancelado" ? (
                            <span
                              style={{
                                backgroundColor: "#f8d7da",
                                color: "#721c24",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                fontSize: "0.85rem",
                                fontWeight: "600",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <i
                                className="pi pi-times"
                                style={{ fontSize: "0.7rem" }}
                              ></i>
                              CANCELADO
                            </span>
                          ) : (
                            <span
                              style={{
                                backgroundColor: "#fff3cd",
                                color: "#856404",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                fontSize: "0.85rem",
                                fontWeight: "600",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <i
                                className="pi pi-clock"
                                style={{ fontSize: "0.7rem" }}
                              ></i>
                              AGENDADO
                            </span>
                          )}

                          {item.tipo_agendamento === "retorno" && (
                            <span
                              style={{
                                backgroundColor: "#cce5ff",
                                color: "#004085",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                fontSize: "0.85rem",
                                fontWeight: "600",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <i
                                className="pi pi-sync"
                                style={{ fontSize: "0.7rem" }}
                              ></i>
                              RETORNO
                            </span>
                          )}
                        </div>

                        {item.status !== "realizado" &&
                          item.status !== "cancelado" && (
                            <div style={{ display: "flex", gap: "5px" }}>
                              <Button
                                icon="pi pi-check"
                                onClick={async () => {
                                  setReservaEditando(item);
                                  setAgendamentoAtualId(item.id); // 🔥 GUARDA O ID DO AGENDAMENTO
                                  fetchTodosDados();
                                  await buscarFichaPaciente(
                                    item.paciente_id,
                                    item.id,
                                  ); // 🔥 PASSA O ID
                                  setDialogFinalizar(true);
                                }}
                                disabled={
                                  !(
                                    user.tipo === "administrator" ||
                                    user.modulos?.Agenda?.editar
                                  )
                                }
                                className="p-button-success p-button-sm"
                                style={{
                                  outline: "none",
                                  boxShadow: "none",
                                  width: "25px",
                                  height: "25px",
                                  borderColor: "#dee2e6",
                                }}
                                tooltip="Realizar atendimento"
                                tooltipOptions={{ position: "top" }}
                              />
                              <Button
                                icon="pi pi-times"
                                onClick={() => {
                                  if (
                                    user.tipo === "administrator" ||
                                    user.modulos?.Agenda?.editar
                                  ) {
                                    openCancelaConfirm(item.id);
                                  }
                                }}
                                disabled={
                                  !(
                                    user.tipo === "administrator" ||
                                    user.modulos?.Agenda?.editar
                                  )
                                }
                                className="p-button-danger p-button-sm"
                                style={{
                                  outline: "none",
                                  boxShadow: "none",
                                  width: "25px",
                                  height: "25px",
                                  borderRadius: "6px",
                                }}
                                tooltip="Cancelar"
                                tooltipOptions={{ position: "top" }}
                              />
                            </div>
                          )}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          marginBottom: "5px",
                          paddingBottom: "5px",
                          borderBottom: "1px solid #e8e8e8",
                        }}
                      >
                        <FaUserCircle
                          style={{
                            fontSize: "2.5rem",
                            color: "#D65A55",
                            flexShrink: 0,
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <h3
                            style={{
                              fontSize: "1rem",
                              fontWeight: "600",
                              color: "#2c3e50",
                              margin: "0",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.paciente_nome}
                          </h3>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              color: "#64748b",
                              fontSize: "0.85rem",
                            }}
                          >
                            <i
                              className="pi pi-phone"
                              style={{ fontSize: "0.7rem" }}
                            />
                            <span>{item.paciente_telefone}</span>
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            color: "#64748b",
                            fontSize: "0.85rem",
                          }}
                        >
                          <i
                            className="pi pi-tag"
                            style={{ fontSize: "0.7rem" }}
                          />
                          <span>{item.id}</span>
                        </div>
                      </div>

                      {visualizacaoResumida && (
                        <>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "6px",
                              marginBottom: "5px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                backgroundColor: "#f8f9fa",
                                border: "1px solid #e8e8e8",
                                padding: "6px",
                                borderRadius: "6px",
                                flexDirection: "column",
                                gap: "5px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                <FaCalendarAlt
                                  style={{
                                    color: "#2ecc71",
                                    fontSize: "0.75rem",
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: "0.8rem",
                                    color: "#2c3e50",
                                    fontWeight: "500",
                                  }}
                                >
                                  {formatarData(item.data_atendimento)}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                <FaClock
                                  style={{
                                    color: "#f39c12",
                                    fontSize: "0.75rem",
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: "0.8rem",
                                    color: "#2c3e50",
                                    fontWeight: "500",
                                  }}
                                >
                                  {formatarHorario(item.horario)}
                                </span>
                              </div>
                            </div>

                            <div
                              style={{
                                display: "flex",
                                backgroundColor: "#f8f9fa",
                                border: "1px solid #e8e8e8",
                                padding: "6px",
                                borderRadius: "6px",
                                flexDirection: "column",
                                gap: "5px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                <FaBuilding
                                  style={{
                                    color: "#3498db",
                                    fontSize: "0.75rem",
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: "0.8rem",
                                    color: "#2c3e50",
                                    fontWeight: "500",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.empresa_nome}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                <FaUserMd
                                  style={{
                                    color: "#9b59b6",
                                    fontSize: "0.75rem",
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: "0.8rem",
                                    color: "#2c3e50",
                                    fontWeight: "500",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.profissional_nome}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div
                            style={{
                              backgroundColor: "#f8f9fa",
                              border: "1px solid #e8e8e8",
                              padding: "6px",
                              borderRadius: "6px",
                              marginBottom: "6px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                              }}
                            >
                              <FaList
                                style={{
                                  color: "#e74c3c",
                                  fontSize: "0.75rem",
                                }}
                              />
                              <span
                                style={{
                                  fontSize: "0.85rem",
                                  color: "#2c3e50",
                                  fontWeight: "500",
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item.servico_nome}
                              </span>
                            </div>
                          </div>
                        </>
                      )}

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "end",
                          paddingTop: "5px",
                          gap: "5px",
                          marginTop: "auto",
                          borderTop: visualizacaoResumida
                            ? "1px solid #e8e8e8"
                            : "",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "5px",
                          }}
                        >
                          <Button
                            icon="pi pi-file-edit"
                            onClick={() => {
                              if (
                                (user.tipo === "administrator" ||
                                  user.modulos?.Agenda?.editar) &&
                                item.status === "confirmado"
                              ) {
                                setReservaEditando(item);
                                fetchTodosDados();
                                setEmpresa(item.empresa_id);
                                setProfissional(item.profissional_id);
                                setServico(item.servico_id);
                                setVisibleEditar(true);
                              }
                            }}
                            disabled={
                              !(
                                user.tipo === "administrator" ||
                                user.modulos?.Agenda?.editar
                              ) || item.status !== "confirmado"
                            }
                            className="p-button-outlined p-button-sm"
                            style={{
                              outline: "none",
                              boxShadow: "none",
                              width: "25px",
                              height: "25px",
                              borderColor: "#dee2e6",
                              color: "#495057",
                            }}
                            tooltip="Editar"
                            tooltipOptions={{ position: "top" }}
                          />
                          <Button
                            icon="pi pi-history"
                            onClick={async () => {
                              setReservaEditando(item);
                              await buscarFichaPaciente(item.paciente_id);
                              setVisibleHistorico(true);
                            }}
                            className="p-button-outlined p-button-sm"
                            style={{
                              outline: "none",
                              boxShadow: "none",
                              width: "25px",
                              height: "25px",
                              borderColor: "#dee2e6",
                              color: "#495057",
                            }}
                            tooltip="Histórico"
                            tooltipOptions={{ position: "top" }}
                          />
                          <Button
                            icon="pi pi-sync"
                            onClick={() => {
                              if (
                                user.tipo === "administrator" ||
                                user.modulos?.Agenda?.editar
                              ) {
                                setReservaEditando(item);
                                fetchTodosDados();
                                setEmpresa(item.empresa_id);
                                setProfissional(item.profissional_id);
                                setServico(item.servico_id);
                                setVisibleRetorno(true);
                              }
                            }}
                            disabled={
                              !(
                                user.tipo === "administrator" ||
                                user.modulos?.Agenda?.editar
                              )
                            }
                            className="p-button-outlined p-button-sm"
                            style={{
                              outline: "none",
                              boxShadow: "none",
                              width: "25px",
                              height: "25px",
                              borderColor: "#dee2e6",
                              color: "#495057",
                              opacity:
                                user.tipo === "administrator" ||
                                user.modulos?.Agenda?.editar
                                  ? 1
                                  : 0.4,
                              cursor:
                                user.tipo === "administrator" ||
                                user.modulos?.Agenda?.editar,
                            }}
                            tooltip="Retorno"
                            tooltipOptions={{ position: "top" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {loading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <ProgressSpinner
              style={{ width: "50px", height: "50px" }}
              strokeWidth="8"
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Componente auxiliar para o Dialog do Caixa
const DialogConfirmarCaixa = ({ visible, onHide, onConfirm }) => {
  return (
    <Dialog
      visible={visible}
      style={{ width: "350px", fontFamily: "'Rajdhani', sans-serif" }}
      onHide={onHide}
      draggable={false}
      closable={false}
      header={null}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "20px",
          padding: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "25px",
            height: "25px",
            borderRadius: "50%",
            color: "#B83732",
            fontSize: "2.2rem",
          }}
        >
          <i className="pi pi-shopping-cart" style={{ fontSize: "2.5rem" }}></i>
        </div>
        <p
          style={{
            textAlign: "center",
            fontSize: "1rem",
            margin: 0,
            color: "#374151",
          }}
        >
          Deseja realizar o fechamento no CAIXA?
        </p>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "12px",
            width: "100%",
          }}
        >
          <Button
            label="NÃO"
            icon="pi pi-times"
            onClick={onHide}
            className="p-button-outlined p-button-sm"
            style={{
              outline: "none",
              boxShadow: "none",
              padding: "8px 24px",
              minWidth: "100px",
              borderColor: "#e2e8f0",
              color: "#718096",
              fontWeight: "500",
              fontSize: "0.875rem",
              borderRadius: "8px",
            }}
          />
          <Button
            label="SIM"
            icon="pi pi-check"
            onClick={onConfirm}
            className="p-button-success p-button-sm"
            style={{
              outline: "none",
              boxShadow: "none",
              padding: "8px 24px",
              minWidth: "100px",
              fontWeight: "500",
              fontSize: "0.875rem",
              borderRadius: "8px",
            }}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default Agendas;
